import React, { useEffect, useState } from "react";
import { Card, Form, Row, Col, Button } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import "./TrancheForm.css";
import Select from "react-select";
import { trancheForm } from "../../yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  createTrancheBot,
  getBaselineModelValues,
  getAllDealSource,
} from "../../service/api";
import { ButtonLoader } from "../ButtonLoader";
import { showNotification } from "../../service/showNotification";

const stateOptions = [
  { value: "CA", label: "California" },
  { value: "TX", label: "Texas" },
  { value: "NV", label: "Nevada" },
];

const TrancheForm = ({
  fetchedData,
  setTrancheData,
  fetchAllTranches,
  opportunityId,
  setOpportunityId,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(trancheForm),
    defaultValues: {
      itcPercent: fetchedData?.ITC_Target ?? "",
      states: fetchedData?.Select_a_State
        ? fetchedData?.Select_a_State?.split(",")
            .map((state) => {
              const option = stateOptions.find(
                (opt) => opt.label.toLowerCase() === state.trim().toLowerCase()
              );
              return option ? option.value : null;
            })
            .filter((value) => value !== null)
        : [],
      internalMarkupPercent: fetchedData?.Internal_Markup ?? "",
      affiliateMarkupPercent: fetchedData?.Affiliate_Markup ?? "",
      assetDealSource: "All", // Set default to "All"
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isAmpVantage, setIsAmpVantage] = useState(false);
  const [dealSourcesOptions, setDealSourcesOptions] = useState([
    { label: "All", value: "All" },
  ]);

  const onSubmit = async (formData) => {
    setIsLoading(true);
    const payload = {
      ...formData,
      assetDealSource: formData.assetDealSource, // Extract value for API payload
      contactName: fetchedData.Contact_Name,
      stage: fetchedData.Stage,
      contactGroup: fetchedData.Contact_Group,
      opportunityYear: fetchedData.Opportunity_Year,
      totalRemainder: fetchedData.Total_Remainder,
      opportunityId,
    };
    try {
      const response = await createTrancheBot(payload);
      console.log("API Success:", response);
      showNotification("success", "Tranche created successfully");
      setTrancheData(null);
      fetchAllTranches();
      setOpportunityId("");
    } catch (error) {
      console.error("API Error:", error);
      showNotification("error", "Failed to create tranche");
    } finally {
      setIsLoading(false);
    }
  };

  const getItcForAmpVantage = async () => {
    const response = await getBaselineModelValues();
    if (response?.data?.data) {
      setValue("itcPercent", response?.data?.data?.ampVantageItcPercent);
      setValue(
        "internalMarkupPercent",
        response?.data?.data?.ampVantage30PercentMarkup
      );
      setValue(
        "affiliateMarkupPercent",
        response?.data?.data?.ampVantage40PercentMarkup
      );
    }
  };
  const getAllDealSourceOptions = async () => {
    try {
      const response = await getAllDealSource();
      const rawData = response?.data?.data || [];

      const cleanedData = rawData
        .filter((item) => item !== null && item !== undefined && item !== "")
        .map((item) => ({
          label: item,
          value: item,
        }));

      const options = [...dealSourcesOptions, ...cleanedData];
      setDealSourcesOptions(options);
    } catch (error) {
      console.log("Failed to fetch deal sources:", error);
    }
  };

  useEffect(() => {
    if (fetchedData?.Contact_Group == "AmpVantage Energy") {
      setIsAmpVantage(true);
      getItcForAmpVantage();
    }
  }, [fetchedData]);

  useEffect(() => {
    getAllDealSourceOptions();
  }, []);

  return (
    <Card className="tranche-form-card p-3">
      <h5 className="mb-4">Enter Tranche Details</h5>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={4}>
            Asset Provider:
          </Form.Label>
          <Col sm={8}>
            <Controller
              name="assetDealSource"
              control={control}
              rules={{ required: "Asset Provider is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={dealSourcesOptions}
                  placeholder="Select Asset Provider"
                  isClearable={false}
                  onChange={(selected) => {
                    field.onChange(selected.value);
                  }}
                  value={dealSourcesOptions.find(
                    (option) => option.value === field.value
                  )}
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      maxHeight: 160, // Approx height for 4 options (40px each)
                      overflowY: "auto",
                    }),
                  }}
                />
              )}
            />
            {errors.assetDealSource && (
              <Form.Text className="text-danger">
                {errors.assetDealSource.message}
              </Form.Text>
            )}
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={4}>
            ITC % :
          </Form.Label>
          <Col sm={8}>
            <Controller
              name="itcPercent"
              control={control}
              rules={{ required: "ITC percent is required" }}
              render={({ field }) => (
                <Form.Control
                  type="number"
                  placeholder="Enter ITC %"
                  {...field}
                  isInvalid={!!errors.itcPercent}
                />
              )}
            />
            {errors.itcPercent && (
              <Form.Text className="text-danger">
                {errors.itcPercent.message}
              </Form.Text>
            )}
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={4}>
            States :
          </Form.Label>
          <Col sm={8}>
            <Controller
              name="states"
              className="react-select"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={stateOptions}
                  placeholder="Select States"
                  isMulti
                  classNamePrefix="react-select"
                  onChange={(selected) =>
                    field.onChange(selected.map((opt) => opt.value))
                  }
                  value={stateOptions.filter((opt) =>
                    field.value.includes(opt.value)
                  )}
                />
              )}
            />
            {errors.states && (
              <Form.Text className="text-danger">
                {errors.states.message}
              </Form.Text>
            )}
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={4}>
            {isAmpVantage ? "30% Markup %" : "Internal Markup %"} :
          </Form.Label>
          <Col sm={8}>
            {/* ampVantage case internalMarkup is 30% Markup Percent */}
            <Controller
              name="internalMarkupPercent"
              control={control}
              rules={{ required: "Internal Markup percent is required" }}
              render={({ field }) => (
                <Form.Control
                  type="number"
                  placeholder={
                    isAmpVantage
                      ? "Enter 30% Markup Percent"
                      : "Enter Internal Markup %"
                  }
                  {...field}
                  isInvalid={!!errors.internalMarkupPercent}
                />
              )}
            />
            {errors.internalMarkupPercent && (
              <Form.Text className="text-danger">
                {errors.internalMarkupPercent.message}
              </Form.Text>
            )}
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3">
          <Form.Label column sm={4}>
            {isAmpVantage ? "40% Markup %" : "Affiliate Markup %"} :
          </Form.Label>
          <Col sm={8}>
            {/* ampVantage case affiliateMarkup is 40% Markup Percent */}
            <Controller
              name="affiliateMarkupPercent"
              control={control}
              rules={{ required: "Affiliate Markup percent is required" }}
              render={({ field }) => (
                <Form.Control
                  type="number"
                  placeholder={
                    isAmpVantage
                      ? "Enter 40% Markup Percent"
                      : "Enter Affiliate Markup %"
                  }
                  {...field}
                  isInvalid={!!errors.affiliateMarkupPercent}
                />
              )}
            />
            {errors.affiliateMarkupPercent && (
              <Form.Text className="text-danger">
                {errors.affiliateMarkupPercent.message}
              </Form.Text>
            )}
          </Col>
        </Form.Group>

        <div className="d-flex justify-content-end mt-5">
          <Button type="submit" disabled={isLoading}>
            {isLoading ? <ButtonLoader /> : "Submit"}
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default TrancheForm;
